import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { compressImage } from "@/lib/fileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Trash, Plus, PaintBrush, UploadSimple, X } from "@phosphor-icons/react";

export default function Admin() {
    return (
        <div className="space-y-6" data-testid="admin-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    Area riservata
                </div>
                <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
                    Amministrazione
                </h1>
            </div>
            <Tabs defaultValue="societa">
                <TabsList className="flex flex-wrap gap-1 h-auto">
                    <TabsTrigger value="societa" data-testid="tab-societa">Società</TabsTrigger>
                    <TabsTrigger value="teams" data-testid="tab-teams">Squadre</TabsTrigger>
                    <TabsTrigger value="users" data-testid="tab-users">Utenti</TabsTrigger>
                    <TabsTrigger value="players" data-testid="tab-players">Giocatori</TabsTrigger>
                    <TabsTrigger value="matches" data-testid="tab-matches">Partite</TabsTrigger>
                    <TabsTrigger value="convocations" data-testid="tab-convocations">Convocazioni</TabsTrigger>
                    <TabsTrigger value="news" data-testid="tab-news">News</TabsTrigger>
                    <TabsTrigger value="comms" data-testid="tab-communications">Comunicazioni</TabsTrigger>
                    <TabsTrigger value="fields" data-testid="tab-fields">Campi</TabsTrigger>
                </TabsList>
                <TabsContent value="societa"><SocietaPanel /></TabsContent>
                <TabsContent value="teams"><TeamsPanel /></TabsContent>
                <TabsContent value="users"><UsersPanel /></TabsContent>
                <TabsContent value="players"><PlayersPanel /></TabsContent>
                <TabsContent value="matches"><MatchesPanel /></TabsContent>
                <TabsContent value="convocations"><ConvocationsPanel /></TabsContent>
                <TabsContent value="news"><NewsPanel /></TabsContent>
                <TabsContent value="comms"><CommsPanel /></TabsContent>
                <TabsContent value="fields"><FieldsPanel /></TabsContent>
            </Tabs>
        </div>
    );
}

/* ----------------------------- Società ----------------------------- */
function SocietaPanel() {
    const { settings, save } = useSettings();
    const [form, setForm] = useState({
        club_name: "",
        logo_url: "",
        primary_hsl: "",
        accent_hsl: "",
        address: "",
        current_season: "",
    });
    useEffect(() => {
        if (settings) setForm({ ...form, ...settings });
        // eslint-disable-next-line
    }, [settings]);

    const presets = [
        { name: "Verde campo", primary: "142 72% 45%" },
        { name: "Rosso Milan", primary: "0 82% 55%" },
        { name: "Nero/Bianco", primary: "0 0% 90%" },
        { name: "Blu Inter", primary: "220 90% 55%" },
        { name: "Arancio", primary: "24 95% 55%" },
        { name: "Viola", primary: "270 70% 60%" },
    ];

    const submit = async (e) => {
        e.preventDefault();
        try {
            await save(form);
            toast.success("Impostazioni aggiornate");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <form onSubmit={submit} className="mt-4 rounded-2xl hairline bg-card p-5 space-y-4">
            <Row label="Nome società">
                <Input
                    data-testid="settings-club-name"
                    value={form.club_name}
                    onChange={(e) => setForm({ ...form, club_name: e.target.value })}
                />
            </Row>
            <Row label="Logo società">
                <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-xl bg-muted/40 grid place-items-center hairline overflow-hidden shrink-0">
                        {form.logo_url ? (
                            <img
                                src={form.logo_url}
                                alt="preview"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Nessun logo
                            </span>
                        )}
                    </div>
                    <div className="flex-1 space-y-2">
                        <label
                            data-testid="settings-logo-upload"
                            className="inline-flex items-center gap-2 rounded-lg hairline bg-background/60 hover:bg-background px-3 h-9 text-xs uppercase tracking-widest font-semibold press cursor-pointer"
                        >
                            <UploadSimple size={14} weight="bold" /> Carica immagine
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    if (f.size > 4 * 1024 * 1024) {
                                        toast.error("Immagine troppo grande (max 4 MB)");
                                        return;
                                    }
                                    const dataUrl = await compressImage(f, {
                                        maxWidth: 400,
                                        quality: 0.9,
                                    });
                                    setForm({ ...form, logo_url: dataUrl });
                                }}
                            />
                        </label>
                        {form.logo_url && (
                            <button
                                type="button"
                                data-testid="settings-logo-remove"
                                onClick={() => setForm({ ...form, logo_url: "" })}
                                className="inline-flex items-center gap-1 text-xs text-destructive press ml-2"
                            >
                                <X size={12} weight="bold" /> Rimuovi
                            </button>
                        )}
                        <div className="text-[11px] text-muted-foreground">
                            PNG o JPG, ridimensionato automaticamente. In alternativa incolla un URL:
                        </div>
                        <Input
                            data-testid="settings-logo-url"
                            value={form.logo_url && form.logo_url.startsWith("data:") ? "" : form.logo_url || ""}
                            placeholder="https://…"
                            onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                        />
                    </div>
                </div>
            </Row>
            <Row label="Colore primario (HSL)">
                <div className="space-y-2">
                    <Input
                        data-testid="admin-settings-theme-color"
                        value={form.primary_hsl}
                        onChange={(e) => {
                            setForm({ ...form, primary_hsl: e.target.value });
                            if (e.target.value) {
                                document.documentElement.style.setProperty(
                                    "--theme-primary",
                                    e.target.value
                                );
                            }
                        }}
                        placeholder="142 72% 45%"
                    />
                    <div className="flex flex-wrap gap-2">
                        {presets.map((p) => (
                            <button
                                key={p.name}
                                type="button"
                                onClick={() => {
                                    setForm({ ...form, primary_hsl: p.primary });
                                    document.documentElement.style.setProperty(
                                        "--theme-primary",
                                        p.primary
                                    );
                                }}
                                className="rounded-full hairline px-3 py-1.5 text-xs flex items-center gap-2 press"
                            >
                                <span
                                    className="w-3 h-3 rounded-full"
                                    style={{
                                        backgroundColor: `hsl(${p.primary})`,
                                    }}
                                />
                                {p.name}
                            </button>
                        ))}
                    </div>
                </div>
            </Row>
            <Row label="Stagione corrente">
                <Input
                    data-testid="settings-current-season"
                    value={form.current_season || ""}
                    placeholder="2025/2026"
                    onChange={(e) => setForm({ ...form, current_season: e.target.value })}
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                    Cambia la stagione (es. 2026/2027) all&apos;inizio di ogni anno sportivo.
                    Verrà mostrata in tutta l&apos;app.
                </div>
            </Row>
            <Row label="Indirizzo">
                <Input
                    value={form.address || ""}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
            </Row>
            <div className="flex justify-end">
                <Button type="submit" data-testid="settings-save" className="press">
                    <PaintBrush size={14} weight="fill" className="mr-2" /> Salva
                </Button>
            </div>
        </form>
    );
}

/* ----------------------------- Teams ----------------------------- */
function TeamsPanel() {
    const [teams, setTeams] = useState([]);
    const [form, setForm] = useState({ name: "", category: "", season: "2025/2026" });
    const load = async () => {
        const { data } = await api.get("/teams");
        setTeams(data);
    };
    useEffect(() => {
        load();
    }, []);
    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/teams", form);
            setForm({ name: "", category: "", season: "2025/2026" });
            toast.success("Squadra creata");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminare la squadra?")) return;
        await api.delete(`/teams/${id}`);
        load();
    };
    return (
        <div className="mt-4 space-y-4">
            <form
                onSubmit={create}
                className="rounded-2xl hairline bg-card p-4 grid gap-3 md:grid-cols-4"
            >
                <Input
                    data-testid="team-name-input"
                    placeholder="Nome"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                />
                <Input
                    placeholder="Categoria (es. Under 14)"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    required
                />
                <Input
                    placeholder="Stagione"
                    value={form.season}
                    onChange={(e) => setForm({ ...form, season: e.target.value })}
                />
                <Button type="submit" data-testid="team-create" className="press">
                    <Plus size={14} className="mr-2" /> Crea
                </Button>
            </form>
            <div className="rounded-2xl hairline bg-card divide-y divide-border/60">
                {teams.map((t) => (
                    <div key={t.id} className="p-4 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold">{t.name}</div>
                            <div className="text-xs text-muted-foreground">
                                {t.category} · {t.season}
                            </div>
                        </div>
                        <button
                            onClick={() => del(t.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                            aria-label="Elimina"
                        >
                            <Trash size={16} weight="bold" />
                        </button>
                    </div>
                ))}
                {teams.length === 0 && (
                    <div className="p-6 text-sm text-muted-foreground">
                        Nessuna squadra ancora creata.
                    </div>
                )}
            </div>
        </div>
    );
}

/* ----------------------------- Users ----------------------------- */
function UsersPanel() {
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        role: "player",
        team_id: "",
    });
    const load = async () => {
        const [u, t] = await Promise.all([api.get("/users"), api.get("/teams")]);
        setUsers(u.data);
        setTeams(t.data);
    };
    useEffect(() => {
        load();
    }, []);
    const create = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, team_id: form.team_id || null };
            await api.post("/users", payload);
            setForm({ name: "", email: "", password: "", role: "player", team_id: "" });
            toast.success("Utente creato");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminare l'utente?")) return;
        await api.delete(`/users/${id}`);
        load();
    };
    return (
        <div className="mt-4 space-y-4">
            <form
                onSubmit={create}
                className="rounded-2xl hairline bg-card p-4 grid gap-3 md:grid-cols-6"
            >
                <Input
                    data-testid="user-name-input"
                    placeholder="Nome"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                />
                <Input
                    data-testid="user-email-input"
                    placeholder="Email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                />
                <Input
                    data-testid="user-password-input"
                    placeholder="Password"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                />
                <Select
                    value={form.role}
                    onValueChange={(v) => setForm({ ...form, role: v })}
                >
                    <SelectTrigger data-testid="user-role-select">
                        <SelectValue placeholder="Ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="admin">Amministratore</SelectItem>
                        <SelectItem value="coach">Allenatore</SelectItem>
                        <SelectItem value="player">Giocatore</SelectItem>
                        <SelectItem value="parent">Genitore</SelectItem>
                    </SelectContent>
                </Select>
                <Select
                    value={form.team_id || "none"}
                    onValueChange={(v) =>
                        setForm({ ...form, team_id: v === "none" ? "" : v })
                    }
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Squadra" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Nessuna</SelectItem>
                        {teams.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                                {t.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button type="submit" data-testid="user-create" className="press">
                    <Plus size={14} className="mr-2" /> Crea
                </Button>
            </form>
            <div className="rounded-2xl hairline bg-card divide-y divide-border/60">
                {users.map((u) => (
                    <div key={u.id} className="p-4 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{u.name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                                {u.email} · {u.role}
                            </div>
                        </div>
                        <button
                            onClick={() => del(u.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                            aria-label="Elimina"
                        >
                            <Trash size={16} weight="bold" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ----------------------------- Players ----------------------------- */
function PlayersPanel() {
    const [players, setPlayers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        birth_year: "",
        team_id: "",
        position: "",
        jersey_number: "",
        user_id: "",
        parent_ids: [],
    });
    const load = async () => {
        const [p, t, u] = await Promise.all([
            api.get("/players"),
            api.get("/teams"),
            api.get("/users"),
        ]);
        setPlayers(p.data);
        setTeams(t.data);
        setUsers(u.data);
    };
    useEffect(() => {
        load();
    }, []);
    const create = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                team_id: form.team_id || null,
                user_id: form.user_id || null,
                birth_year: form.birth_year ? parseInt(form.birth_year) : null,
                jersey_number: form.jersey_number ? parseInt(form.jersey_number) : null,
            };
            await api.post("/players", payload);
            toast.success("Giocatore creato");
            setForm({
                first_name: "",
                last_name: "",
                birth_year: "",
                team_id: "",
                position: "",
                jersey_number: "",
                user_id: "",
                parent_ids: [],
            });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminare il giocatore?")) return;
        await api.delete(`/players/${id}`);
        load();
    };
    const parents = users.filter((u) => u.role === "parent");
    const playerUsers = users.filter((u) => u.role === "player");
    const teamName = (id) => teams.find((t) => t.id === id)?.name || "-";
    return (
        <div className="mt-4 space-y-4">
            <form
                onSubmit={create}
                className="rounded-2xl hairline bg-card p-4 grid gap-3 md:grid-cols-4"
            >
                <Input
                    placeholder="Nome"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    required
                />
                <Input
                    placeholder="Cognome"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    required
                />
                <Input
                    placeholder="Anno nascita"
                    type="number"
                    value={form.birth_year}
                    onChange={(e) => setForm({ ...form, birth_year: e.target.value })}
                />
                <Input
                    placeholder="Numero maglia"
                    type="number"
                    value={form.jersey_number}
                    onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                />
                <Input
                    placeholder="Ruolo (es. Attaccante)"
                    value={form.position}
                    onChange={(e) => setForm({ ...form, position: e.target.value })}
                />
                <Select
                    value={form.team_id || "none"}
                    onValueChange={(v) =>
                        setForm({ ...form, team_id: v === "none" ? "" : v })
                    }
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Squadra" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Nessuna</SelectItem>
                        {teams.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                                {t.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={form.user_id || "none"}
                    onValueChange={(v) =>
                        setForm({ ...form, user_id: v === "none" ? "" : v })
                    }
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Account giocatore" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {playerUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                                {u.name} ({u.email})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button type="submit" className="press">
                    <Plus size={14} className="mr-2" /> Crea
                </Button>
                {parents.length > 0 && (
                    <div className="md:col-span-4">
                        <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                            Genitori collegati
                        </Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {parents.map((p) => {
                                const active = form.parent_ids.includes(p.id);
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() =>
                                            setForm({
                                                ...form,
                                                parent_ids: active
                                                    ? form.parent_ids.filter(
                                                          (x) => x !== p.id
                                                      )
                                                    : [...form.parent_ids, p.id],
                                            })
                                        }
                                        className={`px-3 py-1.5 rounded-full text-xs press hairline ${
                                            active
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : ""
                                        }`}
                                    >
                                        {p.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </form>
            <div className="rounded-2xl hairline bg-card divide-y divide-border/60">
                {players.map((p) => (
                    <div key={p.id} className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-bold">
                            {p.first_name[0]}
                            {p.last_name[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">
                                {p.first_name} {p.last_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {teamName(p.team_id)} · {p.position || "-"} · #
                                {p.jersey_number ?? "-"}
                            </div>
                        </div>
                        <button
                            onClick={() => del(p.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                        >
                            <Trash size={16} weight="bold" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ----------------------------- Matches ----------------------------- */
function MatchesPanel() {
    const [matches, setMatches] = useState([]);
    const [teams, setTeams] = useState([]);
    const [fields, setFields] = useState([]);
    const [form, setForm] = useState({
        team_id: "",
        opponent: "",
        home: true,
        date: "",
        kickoff_time: "10:30",
        meeting_time: "09:00",
        field_id: "",
        field_name: "",
        materials: "Maglia, Pantaloncini, Calzettoni, Tuta, Borraccia",
        notes: "",
    });
    const load = async () => {
        const [m, t, f] = await Promise.all([
            api.get("/matches"),
            api.get("/teams"),
            api.get("/fields"),
        ]);
        setMatches(m.data);
        setTeams(t.data);
        setFields(f.data);
    };
    useEffect(() => {
        load();
    }, []);
    const create = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                field_id: form.field_id || null,
                materials: form.materials
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            await api.post("/matches", payload);
            toast.success("Partita creata");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminare la partita?")) return;
        await api.delete(`/matches/${id}`);
        load();
    };
    return (
        <div className="mt-4 space-y-4">
            <form
                onSubmit={create}
                className="rounded-2xl hairline bg-card p-4 grid gap-3 md:grid-cols-4"
            >
                <Select
                    value={form.team_id}
                    onValueChange={(v) => setForm({ ...form, team_id: v })}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Squadra" />
                    </SelectTrigger>
                    <SelectContent>
                        {teams.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                                {t.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Input
                    placeholder="Avversaria"
                    value={form.opponent}
                    onChange={(e) => setForm({ ...form, opponent: e.target.value })}
                    required
                />
                <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                />
                <div className="flex gap-2">
                    <Input
                        type="time"
                        placeholder="Ritrovo"
                        value={form.meeting_time}
                        onChange={(e) =>
                            setForm({ ...form, meeting_time: e.target.value })
                        }
                    />
                    <Input
                        type="time"
                        placeholder="Inizio"
                        value={form.kickoff_time}
                        onChange={(e) =>
                            setForm({ ...form, kickoff_time: e.target.value })
                        }
                    />
                </div>
                <Select
                    value={form.home ? "home" : "away"}
                    onValueChange={(v) => setForm({ ...form, home: v === "home" })}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="home">Casa</SelectItem>
                        <SelectItem value="away">Trasferta</SelectItem>
                    </SelectContent>
                </Select>
                <Select
                    value={form.field_id || "none"}
                    onValueChange={(v) => {
                        if (v === "none")
                            setForm({ ...form, field_id: "", field_name: "" });
                        else {
                            const f = fields.find((x) => x.id === v);
                            setForm({ ...form, field_id: v, field_name: f?.name || "" });
                        }
                    }}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Campo" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Manuale</SelectItem>
                        {fields.map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                                {f.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Input
                    placeholder="Nome campo (se manuale)"
                    value={form.field_name}
                    onChange={(e) => setForm({ ...form, field_name: e.target.value })}
                />
                <Input
                    placeholder="Materiale (separato da virgole)"
                    value={form.materials}
                    onChange={(e) => setForm({ ...form, materials: e.target.value })}
                    className="md:col-span-3"
                />
                <Textarea
                    placeholder="Note"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="md:col-span-3"
                />
                <Button type="submit" className="press md:col-span-1">
                    <Plus size={14} className="mr-2" /> Crea partita
                </Button>
            </form>
            <div className="rounded-2xl hairline bg-card divide-y divide-border/60">
                {matches.map((m) => (
                    <div key={m.id} className="p-4 flex items-center gap-3">
                        <div className="w-14 shrink-0">
                            <div className="num text-2xl font-black">
                                {new Date(m.date + "T00:00:00").getDate()}
                            </div>
                            <div className="text-[10px] uppercase text-muted-foreground">
                                {new Date(m.date + "T00:00:00").toLocaleDateString(
                                    "it-IT",
                                    { month: "short" }
                                )}
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">
                                vs {m.opponent}{" "}
                                <span className="text-xs text-muted-foreground">
                                    ({m.kickoff_time})
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {m.home ? "Casa" : "Trasferta"} · {m.field_name || "-"}
                            </div>
                        </div>
                        <button
                            onClick={() => del(m.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                        >
                            <Trash size={16} weight="bold" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ----------------------------- Convocations ----------------------------- */
function ConvocationsPanel() {
    const [matches, setMatches] = useState([]);
    const [players, setPlayers] = useState([]);
    const [matchId, setMatchId] = useState("");
    const [selected, setSelected] = useState({});
    const [existing, setExisting] = useState([]);
    const load = async () => {
        const [m, p] = await Promise.all([api.get("/matches"), api.get("/players")]);
        setMatches(m.data);
        setPlayers(p.data);
    };
    useEffect(() => {
        load();
    }, []);
    useEffect(() => {
        if (!matchId) return;
        (async () => {
            const { data } = await api.get(`/matches/${matchId}/convocations`);
            setExisting(data);
            const sel = {};
            data.forEach((c) => (sel[c.player_id] = true));
            setSelected(sel);
        })();
    }, [matchId]);
    const match = matches.find((m) => m.id === matchId);
    const teamPlayers = match
        ? players.filter((p) => p.team_id === match.team_id)
        : [];
    const save = async () => {
        const ids = Object.entries(selected)
            .filter(([, v]) => v)
            .map(([k]) => k);
        try {
            await api.post(`/matches/${matchId}/convocations`, {
                player_ids: ids,
            });
            toast.success("Convocazioni aggiornate");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    return (
        <div className="mt-4 space-y-4">
            <div className="rounded-2xl hairline bg-card p-4">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Partita
                </Label>
                <Select value={matchId} onValueChange={setMatchId}>
                    <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Seleziona una partita" />
                    </SelectTrigger>
                    <SelectContent>
                        {matches.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                                {m.date} · vs {m.opponent}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            {match && (
                <div className="rounded-2xl hairline bg-card p-4 space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Rosa {match.opponent}
                    </div>
                    {teamPlayers.map((p) => (
                        <label
                            key={p.id}
                            className="flex items-center gap-3 rounded-lg p-2 hover:bg-muted/40 press cursor-pointer"
                        >
                            <Checkbox
                                checked={!!selected[p.id]}
                                onCheckedChange={(v) =>
                                    setSelected((s) => ({ ...s, [p.id]: !!v }))
                                }
                            />
                            <div className="flex-1">
                                <div className="font-semibold">
                                    {p.first_name} {p.last_name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {p.position || "-"} · #{p.jersey_number ?? "-"}
                                </div>
                            </div>
                            {existing.find(
                                (c) => c.player_id === p.id && c.status !== "pending"
                            ) && (
                                <span className="text-[10px] uppercase text-primary">
                                    {existing.find((c) => c.player_id === p.id)?.status}
                                </span>
                            )}
                        </label>
                    ))}
                    {teamPlayers.length === 0 && (
                        <div className="text-sm text-muted-foreground">
                            Nessun giocatore nella squadra.
                        </div>
                    )}
                    <div className="flex justify-end pt-2">
                        <Button onClick={save} className="press">
                            Salva convocazioni
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ----------------------------- News ----------------------------- */
function NewsPanel() {
    const [news, setNews] = useState([]);
    const [form, setForm] = useState({
        title: "",
        body: "",
        image_url: "",
        pinned: false,
    });
    const load = async () => {
        const { data } = await api.get("/news");
        setNews(data);
    };
    useEffect(() => {
        load();
    }, []);
    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/news", { ...form, image_url: form.image_url || null });
            setForm({ title: "", body: "", image_url: "", pinned: false });
            toast.success("News pubblicata");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminare la news?")) return;
        await api.delete(`/news/${id}`);
        load();
    };
    return (
        <div className="mt-4 space-y-4">
            <form onSubmit={create} className="rounded-2xl hairline bg-card p-4 grid gap-3">
                <Input
                    placeholder="Titolo"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                />
                <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-xl bg-muted/40 grid place-items-center hairline overflow-hidden shrink-0">
                        {form.image_url ? (
                            <img
                                src={form.image_url}
                                alt="preview"
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Nessuna
                            </span>
                        )}
                    </div>
                    <div className="flex-1 space-y-2">
                        <label
                            data-testid="news-image-upload"
                            className="inline-flex items-center gap-2 rounded-lg hairline bg-background/60 hover:bg-background px-3 h-9 text-xs uppercase tracking-widest font-semibold press cursor-pointer"
                        >
                            <UploadSimple size={14} weight="bold" /> Foto news
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    if (f.size > 6 * 1024 * 1024) {
                                        toast.error("Immagine troppo grande (max 6 MB)");
                                        return;
                                    }
                                    const dataUrl = await compressImage(f, {
                                        maxWidth: 1200,
                                        quality: 0.82,
                                    });
                                    setForm({ ...form, image_url: dataUrl });
                                }}
                            />
                        </label>
                        {form.image_url && (
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, image_url: "" })}
                                className="inline-flex items-center gap-1 text-xs text-destructive press ml-2"
                            >
                                <X size={12} weight="bold" /> Rimuovi
                            </button>
                        )}
                        <Input
                            placeholder="oppure URL immagine (opzionale)"
                            value={form.image_url && form.image_url.startsWith("data:") ? "" : form.image_url}
                            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                        />
                    </div>
                </div>
                <Textarea
                    placeholder="Testo"
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    required
                />
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                        checked={form.pinned}
                        onCheckedChange={(v) => setForm({ ...form, pinned: !!v })}
                    />
                    In evidenza
                </label>
                <Button type="submit" className="press w-fit">
                    <Plus size={14} className="mr-2" /> Pubblica
                </Button>
            </form>
            <div className="rounded-2xl hairline bg-card divide-y divide-border/60">
                {news.map((n) => (
                    <div key={n.id} className="p-4 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{n.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-2">
                                {n.body}
                            </div>
                        </div>
                        <button
                            onClick={() => del(n.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                        >
                            <Trash size={16} weight="bold" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ----------------------------- Comms ----------------------------- */
function CommsPanel() {
    const [items, setItems] = useState([]);
    const [teams, setTeams] = useState([]);
    const [form, setForm] = useState({ title: "", body: "", team_id: "" });
    const load = async () => {
        const [c, t] = await Promise.all([
            api.get("/communications"),
            api.get("/teams"),
        ]);
        setItems(c.data);
        setTeams(t.data);
    };
    useEffect(() => {
        load();
    }, []);
    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/communications", {
                ...form,
                team_id: form.team_id || null,
            });
            setForm({ title: "", body: "", team_id: "" });
            toast.success("Comunicazione pubblicata");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const del = async (id) => {
        if (!window.confirm("Eliminare la comunicazione?")) return;
        await api.delete(`/communications/${id}`);
        load();
    };
    return (
        <div className="mt-4 space-y-4">
            <form onSubmit={create} className="rounded-2xl hairline bg-card p-4 grid gap-3">
                <Input
                    placeholder="Titolo"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                />
                <Select
                    value={form.team_id || "all"}
                    onValueChange={(v) =>
                        setForm({ ...form, team_id: v === "all" ? "" : v })
                    }
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Destinatari" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutta la società</SelectItem>
                        {teams.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                                {t.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Textarea
                    placeholder="Testo"
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    required
                />
                <Button type="submit" className="press w-fit">
                    <Plus size={14} className="mr-2" /> Pubblica
                </Button>
            </form>
            <div className="rounded-2xl hairline bg-card divide-y divide-border/60">
                {items.map((c) => (
                    <div key={c.id} className="p-4 flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{c.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-2">
                                {c.body}
                            </div>
                        </div>
                        <button
                            onClick={() => del(c.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                        >
                            <Trash size={16} weight="bold" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ----------------------------- Fields ----------------------------- */
function FieldsPanel() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ name: "", address: "", notes: "" });
    const load = async () => {
        const { data } = await api.get("/fields");
        setItems(data);
    };
    useEffect(() => {
        load();
    }, []);
    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/fields", form);
            setForm({ name: "", address: "", notes: "" });
            toast.success("Campo creato");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    const del = async (id) => {
        await api.delete(`/fields/${id}`);
        load();
    };
    return (
        <div className="mt-4 space-y-4">
            <form
                onSubmit={create}
                className="rounded-2xl hairline bg-card p-4 grid gap-3 md:grid-cols-4"
            >
                <Input
                    placeholder="Nome campo"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                />
                <Input
                    placeholder="Indirizzo"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
                <Input
                    placeholder="Note"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
                <Button type="submit" className="press">
                    <Plus size={14} className="mr-2" /> Crea
                </Button>
            </form>
            <div className="rounded-2xl hairline bg-card divide-y divide-border/60">
                {items.map((f) => (
                    <div key={f.id} className="p-4 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{f.name}</div>
                            <div className="text-xs text-muted-foreground">
                                {f.address || "-"}
                            </div>
                        </div>
                        <button
                            onClick={() => del(f.id)}
                            className="p-2 rounded-lg hover:bg-destructive/10 text-destructive press"
                        >
                            <Trash size={16} weight="bold" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="grid gap-2 md:grid-cols-[220px_1fr] md:items-start">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground md:pt-3">
                {label}
            </Label>
            <div>{children}</div>
        </div>
    );
}
